import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Device } from '@cloudsign/shared';
import type { Printer } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useSite } from '../../lib/site';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToastOnSuccess } from '../../components/Toast';
import { Badge, Button, Card, CheckboxField, LinkButton, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ActionsCard } from './ActionsCard';
import { AlertList, BASE, PrinterList, alertLabel, batteryText, keys } from './api';
import { HistoryCard } from './HistoryCard';
import { DevicesLive } from './Live';
import { StateBadge } from './DevicesTab';

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-600">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
    </div>
  );
}

function StatusCard({ device, printers }: { device: Device; printers: Printer[] }) {
  const h = device.health;
  const none = t('devicesUi.stat.none');
  const printer = printers.find((p) => p.id === device.printerId);
  const ps = h?.printer;
  return (
    <Card title={t('devicesUi.detail.status')} className="mb-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StateBadge online={device.online} />
        {device.openAlerts.map((k) => (
          <Badge key={k} tone="warn">
            {alertLabel(k)}
          </Badge>
        ))}
        {device.updateAvailable ? <Badge tone="warn">{t('devicesUi.version.updateAvailable')}</Badge> : null}
      </div>
      {h ? (
        <p className="mb-4 text-xs text-slate-600">{t('devicesUi.detail.reportedAt', { time: formatDateTime(h.reportedAt) })}</p>
      ) : (
        <p className="mb-4 text-sm text-slate-600">{t('devicesUi.detail.noHealth')}</p>
      )}
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={t('devicesUi.stat.battery')}>{batteryText(device)}</Stat>
        <Stat label={t('devicesUi.stat.wifi')}>{h?.wifiStrength == null ? none : t('devicesUi.stat.percent', { value: h.wifiStrength })}</Stat>
        <Stat label={t('devicesUi.stat.network')}>{h ? t(`devicesUi.network.${h.network}`) : none}</Stat>
        <Stat label={t('devicesUi.stat.storage')}>{h?.freeStorageMb == null ? none : t('devicesUi.stat.mb', { value: h.freeStorageMb })}</Stat>
        <Stat label={t('devicesUi.stat.queue')}>{h ? h.queueLength : none}</Stat>
        <Stat label={t('devicesUi.stat.lastSignIn')}>{h?.lastSignInAt ? formatDateTime(h.lastSignInAt) : none}</Stat>
        <Stat label={t('devicesUi.stat.lastSeen')}>{device.lastSeenAt ? formatDateTime(device.lastSeenAt) : t('devicesUi.neverSeen')}</Stat>
        <Stat label={t('devicesUi.stat.pairedAt')}>{formatDateTime(device.pairedAt)}</Stat>
        <Stat label={t('devicesUi.stat.app')}>{device.appVersion ?? none}</Stat>
        <Stat label={t('devicesUi.stat.bundle')}>{device.bundleVersion ?? none}</Stat>
        <Stat label={t('devicesUi.stat.os')}>{device.osVersion ?? none}</Stat>
        <Stat label={t('devicesUi.stat.model')}>{device.model ?? none}</Stat>
        <Stat label={t('devicesUi.stat.printer')}>
          {printer ? printer.name : t('devicesUi.noPrinter')}
          {ps ? (
            <span className="block text-xs text-slate-600">
              {t(`devicesUi.printerState.${ps.state}`)}
              {ps.labelsLeft !== null ? `, ${t('devicesUi.printers.labelsLeft', { count: ps.labelsLeft })}` : ''}
            </span>
          ) : null}
        </Stat>
      </dl>
    </Card>
  );
}

function EditCard({ device, printers }: { device: Device; printers: Printer[] }) {
  const qc = useQueryClient();
  const { sites } = useSite();
  const [form, setForm] = useState({ name: device.name, siteId: device.siteId, printerId: device.printerId ?? '', alertsEnabled: device.alertsEnabled });
  useEffect(() => setForm({ name: device.name, siteId: device.siteId, printerId: device.printerId ?? '', alertsEnabled: device.alertsEnabled }), [device]);
  const save = useMutation({
    mutationFn: () =>
      api.patch(`${BASE}/${device.id}`, {
        body: { name: form.name, siteId: form.siteId, printerId: form.printerId || null, alertsEnabled: form.alertsEnabled },
        schema: Device,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  const errs = fieldErrors(save.error);
  const sitePrinters = printers.filter((p) => p.siteId === form.siteId && p.status === 'active');
  const changed =
    form.name !== device.name || form.siteId !== device.siteId || form.printerId !== (device.printerId ?? '') || form.alertsEnabled !== device.alertsEnabled;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card title={t('devicesUi.edit.title')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <ErrorLine error={save.error} />
        </div>
        <TextField label={t('devicesUi.edit.name')} required maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={errs['name']} />
        <SelectField
          label={t('devicesUi.edit.site')}
          hint={t('devicesUi.edit.siteHint')}
          value={form.siteId}
          onChange={(e) => setForm({ ...form, siteId: e.target.value, printerId: '' })}
          error={errs['siteId']}
        >
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('devicesUi.edit.printer')} value={form.printerId} onChange={(e) => setForm({ ...form, printerId: e.target.value })} error={errs['printerId']}>
          <option value="">{t('devicesUi.noPrinter')}</option>
          {sitePrinters.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </SelectField>
        <CheckboxField
          label={t('devicesUi.edit.alerts')}
          hint={t('devicesUi.edit.alertsHint')}
          checked={form.alertsEnabled}
          onChange={(e) => setForm({ ...form, alertsEnabled: e.target.checked })}
        />
        <div className="sm:col-span-2">
          <Button type="submit" variant="primary" disabled={save.isPending || !changed || form.name.trim() === ''}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function AlertsCard({ deviceId }: { deviceId: string }) {
  const q = useQuery({ queryKey: keys.alerts(deviceId), queryFn: () => api.get(`${BASE}/${deviceId}/alerts`, { schema: AlertList }) });
  return (
    <Card title={t('devicesUi.alertHistory.title')}>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : q.data.length === 0 ? (
        <p className="text-sm text-slate-600">{t('devicesUi.alertHistory.empty')}</p>
      ) : (
        <TableWrap caption={t('devicesUi.alertHistory.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('devicesUi.alertHistory.col.kind')}</Th>
              <Th>{t('devicesUi.alertHistory.col.opened')}</Th>
              <Th>{t('devicesUi.alertHistory.col.cleared')}</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="px-4 py-2.5">{alertLabel(a.kind)}</td>
                <td className="px-4 py-2.5">{formatDateTime(a.openedAt)}</td>
                <td className="px-4 py-2.5">{a.clearedAt ? formatDateTime(a.clearedAt) : t('devicesUi.alertHistory.open')}</td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Card>
  );
}

export function DeviceDetailPage() {
  const { id = '' } = useParams();
  const { sites } = useSite();
  const canManage = useCan('sites.manage_devices');
  const canRemote = useCan('devices.remote_actions');
  const q = useQuery({ queryKey: keys.detail(id), queryFn: () => api.get(`${BASE}/${id}`, { schema: Device }) });
  const printers = useQuery({ queryKey: keys.printers('all'), queryFn: () => api.get(`${BASE}/printers`, { schema: PrinterList }) });

  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const device = q.data;
  const siteName = sites.find((s) => s.id === device.siteId)?.name ?? '';
  return (
    <>
      <PageHeader
        title={device.name}
        description={t('devicesUi.detail.description', { kind: t(`devicesUi.kind.${device.kind}`), site: siteName })}
        actions={<LinkButton to="/manage/devices">{t('devicesUi.detail.back')}</LinkButton>}
      />
      <DevicesLive siteIds={[device.siteId]} />
      <StatusCard device={device} printers={printers.data ?? []} />
      {canRemote && device.status === 'active' ? <ActionsCard device={device} canUnpair={canManage} /> : null}
      <HistoryCard deviceId={device.id} />
      {canManage ? <EditCard device={device} printers={printers.data ?? []} /> : null}
      <AlertsCard deviceId={device.id} />
    </>
  );
}

